import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/api";
import { useParams } from "react-router-dom";
import {
    IoChevronDown,
    IoCamera,
    IoPerson,
    IoCalendar,
    IoAlertCircle,
    IoClose,
    IoCheckmarkCircle,
    IoTime,
    IoSearch,
    IoFlag,
    IoLocationSharp,
    IoGrid,
    IoChatbubble,
    IoRefresh,
    IoWarning
} from "react-icons/io5";
import { GiMagicBroom } from "react-icons/gi";
import TicketModal from "../../components/ticketModal";
import { GrShieldSecurity } from "react-icons/gr";
import { getSessionUser } from "../../utils/session";

const Security = () => {
    const { id } = useParams();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterPriority, setFilterPriority] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [updating, setUpdating] = useState(false);
    const [users, setUsers] = useState([]);
    const sessionUser = getSessionUser();
    const isSubAdmin = sessionUser.role === "sub_admin";

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/ticket/event/${id}?source=security`);
            setTickets(res.data.tickets);
        } catch (error) {
            console.error("Error fetching tickets:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await axiosInstance.get(`/auth/staff/${id}`);
            setUsers(res.data.staff || []);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    useEffect(() => {
        fetchTickets();
        fetchUsers();
    }, [id]);

    const getStatusColor = (status) => {
        const colors = {
            pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
            in_progress: "bg-blue-100 text-blue-800 border-blue-200",
            resolved: "bg-green-100 text-green-800 border-green-200",
            rejected: "bg-red-100 text-red-800 border-red-200"
        };
        return colors[status] || colors.pending;
    };

    const getPriorityColor = (priority) => {
        const colors = {
            low: "bg-gray-100 text-gray-700 border-gray-200",
            medium: "bg-blue-100 text-blue-700 border-blue-200",
            high: "bg-orange-100 text-orange-700 border-orange-200",
            critical: "bg-red-100 text-red-700 border-red-200"
        };
        return colors[priority] || colors.medium;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "resolved": return <IoCheckmarkCircle className="w-3 h-3" />;
            case "in_progress": return <IoTime className="w-3 h-3 animate-pulse" />;
            case "rejected": return <IoClose className="w-3 h-3" />;
            default: return <IoAlertCircle className="w-3 h-3" />;
        }
    };

    const calculateTimeRemaining = (deadline, status) => {
        if (status === "resolved") {
            return { text: "Resolved", isOverdue: false };
        }

        const now = new Date();
        const diff = new Date(deadline) - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (diff < 0) return { text: "Overdue", isOverdue: true };
        if (hours < 2) return { text: `${hours}h ${minutes}m left`, isUrgent: true };

        return { text: `${hours}h remaining`, isNormal: true };
    };

    const filteredTickets = tickets.filter(ticket => {
        const matchesStatus = filterStatus === "all" || ticket.status === filterStatus;
        const matchesPriority = filterPriority === "all" || ticket.priorite === filterPriority;
        const matchesSearch = ticket.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.stand.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.location?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesStatus && matchesPriority && matchesSearch;
    });

    const sortedTickets = [...filteredTickets].sort((a, b) => {
        const aOverdue =
            a.status !== "resolved" &&
            a.status !== "rejected" &&
            a.slaDeadline &&
            calculateTimeRemaining(a.slaDeadline, a.status).isOverdue;

        const bOverdue =
            b.status !== "resolved" &&
            b.status !== "rejected" &&
            b.slaDeadline &&
            calculateTimeRemaining(b.slaDeadline, b.status).isOverdue;

        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;

        return new Date(b.createdAt) - new Date(a.createdAt);
    });

    const overdueCount = tickets.filter(
        t =>
            t.status !== "resolved" &&
            t.status !== "rejected" &&
            t.slaDeadline &&
            calculateTimeRemaining(t.slaDeadline, t.status).isOverdue
    ).length;

    const stats = {
        total: tickets.length,
        pending: tickets.filter(t => t.status === "pending").length,
        inProgress: tickets.filter(t => t.status === "in_progress").length,
        resolved: tickets.filter(t => t.status === "resolved").length,
        overdue: overdueCount
    };

    const openModal = (ticket) => {
        setSelectedTicket(ticket);
        document.body.style.overflow = "hidden";
    };

    const closeModal = () => {
        setSelectedTicket(null);
        document.body.style.overflow = "unset";
    };

    const handleStatusChange = async (ticketId, newStatus) => {
        try {
            setUpdating(true);
            const response = await axiosInstance.put(`/ticket/status/${ticketId}`, { status: newStatus });

            const updatedTickets = tickets.map(t =>
                t._id === ticketId ? { ...t, status: newStatus } : t
            );
            setTickets(updatedTickets);

            if (selectedTicket?._id === ticketId) {
                setSelectedTicket({ ...selectedTicket, status: newStatus });
            }

            fetchTickets();
        } catch (error) {
            console.error("Error updating status:", error);
            alert("Failed to update status. Please try again.");
            fetchTickets();
        } finally {
            setUpdating(false);
        }
    };

    const handleAssignTicket = async (ticketId, userId) => {
        try {
            setUpdating(true);
            const response = await axiosInstance.put(`/ticket/assign/${ticketId}`, { assignedUser: userId });

            const assignedUser = users.find(u => u._id === userId);

            const updatedTickets = tickets.map(t =>
                t._id === ticketId ? { ...t, assignedUser: assignedUser } : t
            );
            setTickets(updatedTickets);

            if (selectedTicket?._id === ticketId) {
                setSelectedTicket({ ...selectedTicket, assignedUser: assignedUser });
            }

            fetchTickets();
        } catch (error) {
            console.error("Error assigning ticket:", error);
            alert("Failed to assign ticket. Please try again.");
            fetchTickets();
        } finally {
            setUpdating(false);
        }
    };

    const handlePriorityChange = async (ticketId, newPriority) => {
        try {
            setUpdating(true);
            await axiosInstance.put(`/ticket/${ticketId}`, { priorite: newPriority });

            const updatedTickets = tickets.map(t =>
                t._id === ticketId ? { ...t, priorite: newPriority } : t
            );
            setTickets(updatedTickets);

            if (selectedTicket?._id === ticketId) {
                setSelectedTicket({ ...selectedTicket, priorite: newPriority });
            }

            fetchTickets();
        } catch (error) {
            console.error("Error updating priority:", error);
            alert("Failed to update priority. Please try again.");
            fetchTickets();
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-gray-600 font-medium text-sm">Loading security tickets...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-6">
            <div className="mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-500/30">
                                    <GrShieldSecurity className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-lg font-bold text-gray-900">
                                        Security Tickets
                                    </h1>
                                    <p className="text-gray-600 text-xs">
                                        Manage and monitor all security requests
                                    </p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={fetchTickets}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                        >
                            <IoRefresh className="w-3.5 h-3.5 text-gray-600" />
                            <span className="font-medium text-gray-700 text-xs">Refresh</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-5 gap-2 mb-3">
                        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-gray-600 font-medium">Total</p>
                                    <p className="text-base font-bold text-gray-900">{stats.total}</p>
                                </div>
                                <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <IoGrid className="w-4 h-4 text-gray-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-yellow-600 font-medium">Pending</p>
                                    <p className="text-base font-bold text-yellow-700">{stats.pending}</p>
                                </div>
                                <div className="w-7 h-7 bg-yellow-100 rounded-lg flex items-center justify-center">
                                    <IoAlertCircle className="w-4 h-4 text-yellow-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-blue-600 font-medium">In Progress</p>
                                    <p className="text-base font-bold text-blue-700">{stats.inProgress}</p>
                                </div>
                                <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <IoTime className="w-4 h-4 text-blue-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-green-600 font-medium">Resolved</p>
                                    <p className="text-base font-bold text-green-700">{stats.resolved}</p>
                                </div>
                                <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center">
                                    <IoCheckmarkCircle className="w-4 h-4 text-green-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-red-600 font-medium">Overdue</p>
                                    <p className="text-base font-bold text-red-700">{stats.overdue}</p>
                                </div>
                                <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center">
                                    <IoWarning className="w-4 h-4 text-red-600" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-200">
                        <div className="flex flex-col md:flex-row gap-2">
                            <div className="flex-1 relative">
                                <IoSearch className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by stand, location, or description..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-8 pr-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                                />
                            </div>

                            <div className="flex gap-2">
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-xs"
                                >
                                    <option value="all">All Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="resolved">Resolved</option>
                                    <option value="rejected">Rejected</option>
                                </select>

                                <select
                                    value={filterPriority}
                                    onChange={(e) => setFilterPriority(e.target.value)}
                                    className="px-2.5 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-xs"
                                >
                                    <option value="all">All Priority</option>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {sortedTickets.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
                        <div className="flex justify-center mb-2">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                                <IoAlertCircle className="w-6 h-6 text-gray-400" />
                            </div>
                        </div>
                        <p className="text-gray-500 text-xs font-semibold">No tickets found</p>
                        <p className="text-gray-400 text-xs mt-1">
                            {tickets.length === 0
                                ? "security tickets will appear here when submitted"
                                : "Try adjusting your filters or search query"}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {sortedTickets.map((ticket) => {
                            const timeInfo = ticket.slaDeadline ? calculateTimeRemaining(ticket.slaDeadline) : null;
                            const isOverdue = timeInfo?.isOverdue;

                            return (
                                <div
                                    key={ticket._id}
                                    onClick={() => openModal(ticket)}
                                    className={`relative bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden border border-gray-200 hover:border-blue-300
                                        }`}
                                >
                                    {isOverdue && ticket.status !== "resolved" && ticket.status !== "rejected" && (
                                        <div className="absolute top-0 right-0 bg-red-500 text-white px-2 py-0.5 rounded-bl-lg shadow-md z-10">
                                            <div className="flex items-center gap-1">
                                                <IoWarning className="w-3 h-3 animate-pulse" />
                                                <span className="text-xs font-bold">OVERDUE</span>
                                            </div>
                                        </div>
                                    )}

                                    {ticket.status === "resolved" && (
                                        <div className="absolute top-0 right-0 bg-green-500 text-white px-2 py-0.5 rounded-bl-lg shadow-md z-10">
                                            <span className="text-xs font-bold">RESOLVED</span>
                                        </div>
                                    )}

                                    <div className="p-3">

                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                                                    <IoPerson className="w-4 h-4 text-white" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-900 text-xs">
                                                        {ticket.createdby?.name || "Unknown"}
                                                    </p>
                                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                                        <IoCalendar className="w-2.5 h-2.5" />
                                                        {new Date(ticket.createdAt).toLocaleDateString("en-US", {
                                                            month: "short",
                                                            day: "numeric",
                                                        })}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-1.5 mb-2">
                                            <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-md">
                                                <IoGrid className="w-3 h-3 text-blue-600" />
                                                <span className="text-xs font-medium text-blue-900">{ticket.stand}</span>
                                            </div>
                                            {ticket.location && (
                                                <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-md">
                                                    <IoLocationSharp className="w-3 h-3 text-gray-600" />
                                                    <span className="text-xs text-gray-700">{ticket.location}</span>
                                                </div>
                                            )}
                                        </div>

                                        <p className="text-gray-700 text-xs leading-relaxed mb-3 line-clamp-2">
                                            {ticket.description}
                                        </p>

                                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                            <div className="flex items-center gap-1.5">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                                                    {getStatusIcon(ticket.status)}
                                                    {ticket.status.replace('_', ' ').toUpperCase()}
                                                </span>

                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${getPriorityColor(ticket.priorite)}`}>
                                                    <IoFlag className="w-2.5 h-2.5" />
                                                    {ticket.priorite.toUpperCase()}
                                                </span>
                                            </div>

                                            <div className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors">
                                                <IoChevronDown className="w-3 h-3 -rotate-90 text-gray-600" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {selectedTicket && (
                <TicketModal
                    ticket={selectedTicket}
                    onClose={closeModal}
                    onStatusChange={handleStatusChange}
                    onAssignUser={handleAssignTicket}
                    onPriorityChange={handlePriorityChange}
                    updating={updating}
                    getStatusColor={getStatusColor}
                    getPriorityColor={getPriorityColor}
                    getStatusIcon={getStatusIcon}
                    users={users}
                    isSubAdmin={isSubAdmin}
                />
            )}
        </div>
    );
};

export default Security;